import { useState } from "react";
import { toast } from "sonner";
import { useShowConversation } from "@/hooks/conversation/useConversations";
import {
  AI_TARGETS,
  buildAnalysisPrompt,
  buildTranscriptText,
  normalizeTranscript,
  transcriptFileName,
  TranscriptTextParams,
} from "@/lib/conversation/transcript";
import { isRecordedMeeting } from "@/lib/conversation/recordedMeeting";
import { saveBlob } from "@/pages/UserHub/Inbox/Views/components/exportFile";
import { Conversation } from "@/types/conversation/conversation";
import { AiTarget, TranscriptLine } from "@/types/conversation/transcript";

export interface UseTranscriptCopyParams {
  /**
   * A full Conversation (detail surfaces + inbox rows). When the transcript
   * isn't already on the object, the hook fetches it on demand so list rows
   * can copy without first opening the conversation.
   */
  conversation?: Conversation;
  /** Already-normalized transcript lines, when a surface holds them (detail). */
  transcript?: TranscriptLine[];
  /** Direct fields for surfaces that aren't a Conversation (e.g. Insights). */
  genieName?: string;
  dateRaw?: string | null;
  durationSeconds?: number | null;
  /** Raw (un-normalized) transcript array for non-Conversation surfaces. */
  rawTranscript?: unknown;
  /** A meeting rather than a customer conversation. Derived from `conversation.type` when omitted. */
  isMeeting?: boolean;
}

/**
 * Centralizes "grab this transcript" — copy to clipboard, download as .txt, or
 * copy + open an AI assistant — across every surface (detail page, transcript tab, inbox rows,
 * insights). Resolves the transcript from whatever the caller has on hand and,
 * for inbox rows where the list payload omits it, fetches on demand.
 */
export function useTranscriptCopy(params: UseTranscriptCopyParams) {
  const [copied, setCopied] = useState(false);
  const { mutateAsync: showConversation, isPending: isFetching } = useShowConversation();

  const resolve = async (): Promise<TranscriptTextParams | null> => {
    const details = params.conversation?.details;
    let genieName = params.genieName ?? params.conversation?.agent?.name ?? "";
    let dateRaw = params.dateRaw ?? details?.conversation_date ?? details?.created_at ?? null;
    let durationSeconds = params.durationSeconds ?? details?.duration ?? null;

    // 1. Lines passed in (detail surfaces). May still be in the raw
    // `{speaker, text}` recording shape, so normalize defensively.
    let lines: TranscriptLine[] | null =
      params.transcript && params.transcript.length ? normalizeTranscript(params.transcript) : null;

    // 2. Raw transcript on hand (non-Conversation surfaces, or list payload).
    if (!lines) {
      const fromRaw = normalizeTranscript(params.rawTranscript ?? params.conversation?.details?.transcript);
      if (fromRaw.length) lines = fromRaw;
    }

    // 3. Fetch on demand (inbox rows whose list payload omits the transcript).
    if (!lines && params.conversation) {
      const { conversation: fresh } = await showConversation(params.conversation);
      const freshDetails = fresh?.details;
      const fetched = normalizeTranscript(freshDetails?.transcript);
      if (fetched.length) {
        lines = fetched;
        genieName = genieName || fresh?.agent?.name || "";
        dateRaw = dateRaw ?? freshDetails?.conversation_date ?? freshDetails?.created_at ?? null;
        durationSeconds = durationSeconds ?? freshDetails?.duration ?? null;
      }
    }

    if (!lines || !lines.length) return null;
    return { genieName, dateRaw, durationSeconds, transcript: lines };
  };

  const copy = async () => {
    try {
      const resolved = await resolve();
      if (!resolved) {
        toast.error("No transcript available for this conversation yet.");
        return;
      }
      await navigator.clipboard.writeText(buildTranscriptText(resolved));
      setCopied(true);
      toast.success("Transcript copied to your clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the transcript. Try again.");
    }
  };

  const sendToAi = async (target: AiTarget) => {
    try {
      const resolved = await resolve();
      if (!resolved) {
        toast.error("No transcript available for this conversation yet.");
        return;
      }
      const isMeeting = params.isMeeting ?? isRecordedMeeting(params.conversation?.type);
      await navigator.clipboard.writeText(
        buildAnalysisPrompt(resolved.genieName, isMeeting) + buildTranscriptText(resolved),
      );
      window.open(target.url, "_blank", "noopener,noreferrer");
      toast.success(`Transcript copied — paste it into ${target.label} to analyze.`);
    } catch {
      toast.error("Couldn't copy the transcript. Try again.");
    }
  };

  const download = async () => {
    try {
      const resolved = await resolve();
      if (!resolved) {
        toast.error("No transcript available for this conversation yet.");
        return;
      }
      saveBlob(
        new Blob([buildTranscriptText(resolved)], { type: "text/plain;charset=utf-8" }),
        transcriptFileName(resolved.genieName, resolved.dateRaw),
      );
    } catch {
      toast.error("Couldn't download the transcript. Try again.");
    }
  };

  return { copy, download, sendToAi, copied, isPreparing: isFetching, targets: AI_TARGETS };
}
